import {
    BaseEntryResultType,
    ButtonClickAnalyticsType,
    FilterProps,
    FilterValues,
    SupportedKmsTypeFilter,
} from "@kaltura/mediaspace-shared-types";
import {
    FilterBarButtonWithModal,
    FilterMenuProps,
    SearchBar,
    SearchResultsContainer,
} from "@kaltura/mediaspace-shared-search";
import { useContext, useEffect, useMemo, useState } from "react";
import {
    baseUrl,
    extractDefaultFilterValues,
    removeEmptyDefaultFilterValues,
    translate,
    useMediaQuery,
} from "@kaltura/mediaspace-shared-utils";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { buildQuerystring } from "kaltura-typescript-client/adapters/utils";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import RejectContainer, { RejectContainerProps } from "./card-actions/reject/reject-container";
import ApproveContainer, { ApproveContainerProps } from "./card-actions/approve/approve-container";
import { StyledAllContentContainer, StyledResultsContainer } from "./styled-components";
import ExploreActions from "./explore-actions/explore-actions";
import { SearchResultsData } from "@kaltura/mediaspace-shared-selectable-media-list";
import NoMedia from "./no-media/NoMedia";
import { MediaCardContext, SearchResultsSkeleton } from "@kaltura/mediaspace-shared-media-list";
import { useTheme } from "@kaltura/mediaspace-shared-styled";

export interface CategoryModerationProps {
    /** is it channel or gallery moderation page */
    isChannel: boolean;
    /** category id */
    categoryId: number;
    /** filers definitions */
    filters: FilterProps<SupportedKmsTypeFilter>[];
    /** props passed down to the search bar's filter menu */
    filterMenuProps: FilterMenuProps;
    /** initial values for the filters */
    initialValues?: FilterValues;
    /** number of entries on one page in the response */
    pageSize: number;
    /** show confirmation popup before approving or rejecting media */
    requestConfirmation: boolean;
    /** threshold for creating bulk async actions for approving/rejecting media */
    bulkThreshold: number;
    /** base class name for the component */
    baseClassName?: string;
}

/**
 * category moderation shared component
 * handles searching pending moderation content
 * and allows approving/rejecting it
 * used in the category moderation page
 * and the category edit content moderation tab
 */
export const CategoryModeration = (props: CategoryModerationProps) => {
    const {
        isChannel,
        categoryId,
        filters,
        filterMenuProps,
        initialValues = {},
        pageSize,
        requestConfirmation,
        bulkThreshold,
        baseClassName,
    } = props;

    const moduleName = isChannel ? "channelmoderation" : "categorymoderation";
    const approveUrl = `${baseUrl}/${moduleName}/index/approve-entries/categoryId/${categoryId}`;
    const rejectUrl = `${baseUrl}/${moduleName}/index/reject-entries/categoryId/${categoryId}`;

    const { showToast } = useToastsContext();

    const theme = useTheme();
    const isXsScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));

    // filter values as the user set them (+ defaults)
    const [filterValues, setFilterValues] = useState(() => {
        return { ...extractDefaultFilterValues(filters), ...initialValues };
    });

    const [isBulkInProgress, setIsBulkInProgress] = useState(false);
    const [selectedSearchResult, setSelectedSearchResult] = useState<BaseEntryResultType>();

    /**
     * when this value changes the key prop of the search-results component will change,
     * forcing a re-render (and therefore reload search results).
     */
    const [forceRerenderToggle, setForceRerenderToggle] = useState(false);

    useEffect(() => {
        // add event listeners to relevant async-jobs-done events
        const switchAsyncProgressOff = () => {
            showToast({
                severity: "success",
                message: translate("Async process completed successfully, refresh the page to see updated content."),
            });
        };
        window.addEventListener(`approve-category-entries_${categoryId}`, switchAsyncProgressOff);
        window.addEventListener(`reject-category-entries_${categoryId}`, switchAsyncProgressOff);

        return () => {
            window.removeEventListener(`approve-category-entries_${categoryId}`, switchAsyncProgressOff);
            window.removeEventListener(`reject-category-entries_${categoryId}`, switchAsyncProgressOff);
        };
    }, [categoryId]);

    // handle filter change
    const handleFilterChange = (values: FilterValues) => {
        setFilterValues(values);
        setSelectedSearchResult(undefined);
    };

    // handle submit search form
    const handleKeywordSearchSubmit = (value: string) => {
        setFilterValues((filterValues) => ({
            ...filterValues,
            keyword: value,
        }));
        setSelectedSearchResult(undefined);
    };

    // handle filter menu change
    const handleFilterMenuChange = (values: FilterValues) => {
        setFilterValues((filterValues) => ({ ...filterValues, ...values }));
        setSelectedSearchResult(undefined);
    };

    const reloadMedia = () => {
        // force re-rendering search results component (which triggers data reload)
        setForceRerenderToggle((oldval) => !oldval);
        // close explore panel
        setSelectedSearchResult(undefined);
    };

    const handleDataReload = (data: any) => {
        if (data) {
            const { bulkInProgress } = data;
            setIsBulkInProgress(bulkInProgress);
        }
    };

    // minimal filter values - no empties, no defaults
    // will be recalculated on filters or filterValues change
    const minFilterValues = useMemo(
        () => removeEmptyDefaultFilterValues(filters, filterValues),
        [filters, filterValues]
    );

    // update history on filter change
    useEffect(() => {
        const updateHistory = (filterValues: FilterValues) => {
            // update query string
            const queryString = buildQuerystring(filterValues);
            window.history.pushState(null, "", `?${queryString}`);
        };

        updateHistory(minFilterValues);
    }, [filters, minFilterValues]);

    // check from context if this is kaf
    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;

    // always show button to open filters modal
    const searchBarButtons = [
        <FilterBarButtonWithModal filters={filters} values={filterValues} onChange={handleFilterChange} />,
    ];

    // the selected filter menu value
    const filterMenuValue = filterValues[filterMenuProps.param];
    // the selected filter menu label
    const filterMenuLabel =
        filterMenuValue === filterMenuProps.defaultValue
            ? undefined
            : filterMenuProps.items.find((item) => item.value === filterMenuValue)?.label;

    const bulkButtonActions = useMemo(
        () => [
            {
                name: "reject-component",
                component: RejectContainer,
                props: {
                    url: rejectUrl,
                    requestConfirmation,
                    forceLargeButton: true,
                    noTextBreakpoint: "sm",
                    bulkThreshold: bulkThreshold,
                    disabled: isBulkInProgress,
                    analytics: {
                        action: "bulk actions - reject executed",
                        buttonType: ButtonClickAnalyticsType.DELETE,
                    },
                    entryName: "",
                } as RejectContainerProps,
            },
            {
                name: "approve-component",
                component: ApproveContainer,
                props: {
                    url: approveUrl,
                    requestConfirmation,
                    forceLargeButton: true,
                    noTextBreakpoint: "sm",
                    bulkThreshold: bulkThreshold,
                    disabled: isBulkInProgress,
                    analytics: {
                        action: "bulk actions - approve executed",
                        buttonType: ButtonClickAnalyticsType.ADD,
                    },
                    entryName: "",
                } as ApproveContainerProps,
            },
        ],
        [isBulkInProgress, requestConfirmation, bulkThreshold, approveUrl, rejectUrl]
    );

    return (
        <MediaCardContext.Provider
            value={{
                categoryId: categoryId,
                openLinksInNewTab: !isKaf,
                displayActionsInline: true,
                showThumbnail: !isXsScreen,
            }}
        >
            <StyledAllContentContainer
                className={`${baseClassName}-content-container`}
                baseClassName={baseClassName}
                onClose={() => setSelectedSearchResult(undefined)}
                searchResult={selectedSearchResult}
                filterValues={minFilterValues}
                showPlayer={true}
                mediaContext={`${moduleName}/categoryId/${categoryId}`}
                categoryId={categoryId}
                callToActionComponent={
                    selectedSearchResult && (
                        <ExploreActions
                            reloadMedia={reloadMedia}
                            approveUrl={approveUrl}
                            rejectUrl={rejectUrl}
                            entryId={selectedSearchResult.object.id}
                            entryName={selectedSearchResult.object.name}
                            requestConfirmation={requestConfirmation}
                            disabled={isBulkInProgress}
                        />
                    )
                }
            >
                <SearchBar
                    buttons={searchBarButtons}
                    keywordSearchProps={{
                        onSubmit: handleKeywordSearchSubmit,
                        value: filterValues["keyword"] as string,
                        placeholder: translate("Search"),
                        ariaLabel: translate("Search"),
                    }}
                    fullWidth={isXsScreen}
                    filterMenuProps={{
                        ...filterMenuProps,
                        values: filterValues,
                        onChange: handleFilterMenuChange,
                    }}
                />

                <StyledResultsContainer className={`${baseClassName}-results-container`}>
                    <SearchResultsContainer
                        key={forceRerenderToggle ? "some_dummy" : "another_dummy"}
                        searchUrl={`${baseUrl}/${moduleName}/index/search-results/categoryId/${categoryId}`}
                        isWideLayout={!selectedSearchResult}
                        filterValues={minFilterValues}
                        pageSize={pageSize}
                        onEntryShowMore={(item) => setSelectedSearchResult(item)}
                        searchInLabel={filterMenuLabel}
                        searchResultsDisplayComponent={SearchResultsData}
                        searchResultsSkeletonComponent={SearchResultsSkeleton}
                        bulkButtonActions={bulkButtonActions}
                        displayComponentProps={{
                            noMediaComponent: NoMedia,
                            onData: handleDataReload,
                            additionalInvalidation: () => setSelectedSearchResult(undefined),
                        }}
                    />
                </StyledResultsContainer>
            </StyledAllContentContainer>
        </MediaCardContext.Provider>
    );
};

export default CategoryModeration;
